import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SplitHeading } from './motion/SplitHeading';
import { EASE_WIPE } from '../lib/motion';
import { markLoaderSeen } from '../lib/loader';
import { lockScroll } from '../lib/smoothScroll';
import '../styles/loader/loader.css';

interface LoaderProps {
    /** The panel starts lifting: the page underneath can begin its entrance. */
    onReveal: () => void;
    /** The panel is gone. */
    onDone: () => void;
}

const HOLD_MS = 2100;

/*
 * First visit only. The wordmark assembles from its letters (centre outward) on
 * ink, holds, then the whole panel wipes upward to reveal the page. No spinner,
 * no counter. Based on schmitt-design: studiox-split-heading + studiox-shape-loader
 * (the wipe), with the wordmark standing in for the brand shape.
 */
const Loader = ({ onReveal, onDone }: LoaderProps) => {
    const [leaving, setLeaving] = useState(false);

    useEffect(() => {
        lockScroll(true);
        document.body.style.overflow = 'hidden';
        const timer = window.setTimeout(() => {
            setLeaving(true);
            onReveal();
        }, HOLD_MS);
        return () => {
            window.clearTimeout(timer);
            document.body.style.overflow = '';
            lockScroll(false);
        };
    }, [onReveal]);

    return (
        <motion.div
            className="loader"
            aria-hidden="true"
            initial={{ clipPath: 'inset(0% 0% 0% 0%)' }}
            animate={{ clipPath: leaving ? 'inset(0% 0% 100% 0%)' : 'inset(0% 0% 0% 0%)' }}
            transition={{ duration: 0.9, ease: EASE_WIPE }}
            onAnimationComplete={() => {
                if (!leaving) return;
                markLoaderSeen();
                onDone();
            }}
        >
            <SplitHeading as="p" className="loader__word" text="Schmitt Software" play />
            <span className="loader__bar" aria-hidden="true" />
            <motion.span
                className="label loader__tag"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.1 }}
            >
                Simple, reliable software
            </motion.span>
        </motion.div>
    );
};

export default Loader;
